import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getPaymentMethods,
  paymentSuccess,
} from "../../redux/reducers/Payments/PaymentReducer";
import downloadReceipt from "utils/helpers/downloadReceipt";
import { AppDispatch, RootState } from "redux/store";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { PaymentMethod } from "redux/reducers/Payments/PaymentReducerTypes";
import { currencySymbol } from "shared/CurrencySymbol";
import moment from "moment";

// ─── Confetti ─────────────────────────────────────────────────────────────────
interface Particle {
  id: number; x: number; color: string;
  size: number; delay: number; duration: number;
  rotation: number; shape: "rect" | "circle";
}
const CONFETTI_COLORS = ["#22c55e","#3b82f6","#f59e0b","#ec4899","#8b5cf6","#06b6d4"];

const ConfettiPiece = ({ p }: { p: Particle }) => (
  <div style={{
    position: "fixed", left: `${p.x}%`, top: "-20px",
    width: `${p.size}px`,
    height: p.shape === "rect" ? `${p.size * 0.4}px` : `${p.size}px`,
    backgroundColor: p.color,
    borderRadius: p.shape === "circle" ? "50%" : "2px",
    animation: `confettiFall ${p.duration}s ${p.delay}s ease-in forwards`,
    transform: `rotate(${p.rotation}deg)`,
    zIndex: 100, pointerEvents: "none",
  }} />
);

const AnimatedCheck = () => (
  <svg viewBox="0 0 52 52" style={{ width: 64, height: 64 }}>
    <circle cx="26" cy="26" r="25" fill="none" stroke="#22c55e" strokeWidth="2"
      style={{ strokeDasharray: 166, strokeDashoffset: 166,
        animation: "strokeCircle 0.6s cubic-bezier(0.65,0,0.45,1) forwards" }} />
    <path fill="none" stroke="#22c55e" strokeWidth="3" strokeLinecap="round"
      strokeLinejoin="round" d="M14.1 27.2l7.1 7.2 16.7-16.8"
      style={{ strokeDasharray: 48, strokeDashoffset: 48,
        animation: "strokeCheck 0.3s 0.6s cubic-bezier(0.65,0,0.45,1) forwards" }} />
  </svg>
);

const DetailRow = ({ icon, label, value, delay, tag }: {
  icon: React.ReactNode; label: string;
  value: string | number | undefined; delay: number;
  tag?: { text: string; color: string; bg: string };
}) => (
  <div style={{
    display: "flex", alignItems: "center", gap: "12px",
    padding: "12px 0", borderBottom: "1px solid #f1f5f9",
    animation: `slideUp 0.4s ${delay}s both`, opacity: 0,
  }}>
    <div style={{ width: 36, height: 36, borderRadius: 10, background: "#f0fdf4",
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {icon}
    </div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 500,
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14, color: "#1e293b", fontWeight: 600 }}>
          {value ?? "—"}
        </span>
        {tag && (
          <span style={{
            fontSize: 10, fontWeight: 700, padding: "2px 8px",
            borderRadius: 20, background: tag.bg, color: tag.color,
            letterSpacing: "0.04em", textTransform: "uppercase",
          }}>
            {tag.text}
          </span>
        )}
      </div>
    </div>
  </div>
);

// ─── SVG icons ─────────────────────────────────────────────────────────────
const IconCalendar = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);
const IconClock = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
  </svg>
);
const IconGuests = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const IconPin = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconDoor = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 21h18M9 21V5a2 2 0 012-2h2a2 2 0 012 2v16"/>
    <circle cx="14.5" cy="13" r="0.5" fill="#22c55e"/>
  </svg>
);
const IconDoc = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
  </svg>
);
const IconCard = () => (
  <svg width="18" height="18" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24">
    <rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/>
  </svg>
);
const IconDownload = () => (
  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 10v6m0 0l-3-3m3 3l3-3"/>
    <path d="M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2"/>
  </svg>
);

// ─── Main component ───────────────────────────────────────────────────────────
const PaymentSuccess = () => {
  const location      = useLocation();
  const dispatch      = useDispatch<AppDispatch>();
  const query         = new URLSearchParams(location.search);
  const status        = query.get("redirect_status");
  const paymentMethod = query.get("payment_method") || "";
  const intentId      = query.get("payment_intent") || "";

  const [confirming,  setConfirming]  = useState(true);
  const [confirmed,   setConfirmed]   = useState(false);
  const [error,       setError]       = useState(false);
  const [particles,   setParticles]   = useState<Particle[]>([]);
  const [showContent, setShowContent] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const reserveBooking = useSelector((state: RootState) => state.payment.reserveBooking);
  const booking        = reserveBooking?.booking;
  const totalAmount    = Number(booking?.total_amount ?? booking?.total ?? 0);

  // Shared property fields — now available after paymentSuccess.fulfilled
  // updates the Redux state (fixed in PaymentReducer.ts)
  const isShared  = booking?.is_shared_property;
  const roomName  = booking?.booked_room;

  const spawnConfetti = () => {
    const p: Particle[] = Array.from({ length: 80 }, (_, i) => ({
      id: i, x: Math.random() * 100,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      size: Math.random() * 8 + 6,
      delay: Math.random() * 1.5,
      duration: Math.random() * 2 + 2,
      rotation: Math.random() * 360,
      shape: Math.random() > 0.5 ? "rect" : "circle",
    }));
    setParticles(p);
    setTimeout(() => setParticles([]), 5000);
  };

  useEffect(() => {
    const confirmBooking = async () => {
      try {
        const pmRes = await dispatch(getPaymentMethods());
        if (pmRes?.payload?.success) {
          const payment = pmRes?.payload?.data?.find(
            (item: PaymentMethod) => item.name?.toLowerCase() === paymentMethod
          );
          const payload = {
            booking: {
              status: "confirmed",
              payment_intent_id: intentId || null,
              payments_attributes: [{
                payment_method_id: payment?.id,
                amount: totalAmount,
                state: status === "succeeded" ? "paid" : "failed",
                transaction_id: intentId,
              }],
            },
          };
          const action = await dispatch(paymentSuccess(payload as any));
          if (action.type === "payment/paymentSuccess/fulfilled") {
            setConfirmed(true);
            setConfirming(false);
            setTimeout(() => { setShowContent(true); spawnConfetti(); }, 300);
          } else {
            setError(true); setConfirming(false);
          }
        }
      } catch {
        setError(true); setConfirming(false);
      }
    };
    confirmBooking();
  }, []);

  // ── Download receipt ────────────────────────────────────────────────────────
  const handleDownloadReceipt = async () => {
    if (!booking?.id || !booking?.number) return;
    setDownloading(true);
    try {
      await downloadReceipt(booking.id, booking.number);
    } catch (err) {
      console.error("Receipt download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const propertyImage = booking?.property?.place_images?.[0];
  const guestName     = booking?.user?.first_name
    ? `${booking.user.first_name} ${booking.user.last_name ?? ""}`.trim()
    : null;

  return (
    <>
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
        @keyframes strokeCircle { 100% { stroke-dashoffset: 0; } }
        @keyframes strokeCheck  { 100% { stroke-dashoffset: 0; } }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      {particles.map(p => <ConfettiPiece key={p.id} p={p} />)}

      <div style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f0fdf4 0%, #eff6ff 50%, #fdf4ff 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2rem 1rem",
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}>

        {/* Loading */}
        {confirming && (
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: 56, height: 56,
              border: "3px solid #e2e8f0", borderTopColor: "#22c55e",
              borderRadius: "50%", animation: "spin 0.8s linear infinite",
              margin: "0 auto 16px",
            }} />
            <p style={{ color: "#64748b", fontSize: 15, fontWeight: 500 }}>
              Confirming your booking…
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{
            background: "white", borderRadius: 20, padding: "2.5rem",
            maxWidth: 400, textAlign: "center",
            boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
            <h2 style={{ color: "#dc2626", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
              Something went wrong
            </h2>
            <p style={{ color: "#64748b", marginBottom: 24 }}>
              We couldn't confirm your booking. Please contact support with your reference below.
            </p>
            {intentId && (
              <p style={{ fontSize: 12, color: "#94a3b8", fontFamily: "monospace" }}>
                Ref: {intentId}
              </p>
            )}
          </div>
        )}

        {/* Success */}
        {confirmed && (
          <div style={{ width: "100%", maxWidth: 520, animation: "fadeIn 0.5s ease both" }}>

            {/* Header */}
            <div style={{
              background: "white", borderRadius: "24px 24px 0 0",
              padding: "2.5rem 2rem 1.5rem", textAlign: "center",
              boxShadow: "0 4px 0 #22c55e",
            }}>
              <div style={{ marginBottom: 16 }}><AnimatedCheck /></div>
              <h1 style={{
                fontSize: 28, fontWeight: 800, color: "#0f172a",
                marginBottom: 6, letterSpacing: "-0.02em",
              }}>
                Booking Confirmed!
              </h1>
              {guestName && (
                <p style={{ color: "#64748b", fontSize: 15 }}>
                  You're all set, <strong style={{ color: "#0f172a" }}>{guestName}</strong> 🎉
                </p>
              )}
              <div style={{
                display: "inline-block", marginTop: 20,
                padding: "10px 28px", borderRadius: 100,
                background: "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "white", fontSize: 22, fontWeight: 800,
                boxShadow: "0 4px 20px rgba(34,197,94,0.35)",
                animation: showContent ? "slideUp 0.4s 0.1s both" : "none",
              }}>
                {currencySymbol()} {Number(totalAmount).toFixed(2)}
              </div>
            </div>

            {/* Property image */}
            {propertyImage && (
              <div style={{ height: 140, overflow: "hidden", position: "relative" }}>
                <img src={propertyImage} alt={booking?.property?.title}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <div style={{
                  position: "absolute", inset: 0,
                  background: "linear-gradient(to bottom, transparent 40%, rgba(0,0,0,0.6))",
                }} />
                <div style={{ position: "absolute", bottom: 12, left: 16, right: 16, color: "white" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 700 }}>{booking?.property?.title}</span>
                    {/* Shared property badge on the image */}
                    {isShared && (
                      <span style={{
                        fontSize: 10, fontWeight: 700,
                        padding: "2px 8px", borderRadius: 20,
                        background: "rgba(59,130,246,0.85)",
                        backdropFilter: "blur(4px)",
                        color: "white", letterSpacing: "0.04em",
                        textTransform: "uppercase",
                      }}>
                        Shared
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>
                    {[booking?.property?.city, booking?.property?.state, booking?.property?.country]
                      .filter(Boolean).join(", ")}
                  </div>
                </div>
              </div>
            )}

            {/* Details card */}
            <div style={{
              background: "white", borderRadius: "0 0 24px 24px",
              padding: "1.5rem 2rem 2rem",
              boxShadow: "0 20px 60px rgba(0,0,0,0.08)",
            }}>
              {/* Booking ref + status */}
              <div style={{
                display: "flex", alignItems: "center",
                justifyContent: "space-between", marginBottom: 20,
              }}>
                <div>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600,
                    textTransform: "uppercase", letterSpacing: "0.06em" }}>
                    Booking Reference
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a",
                    letterSpacing: "0.04em", fontFamily: "monospace" }}>
                    #{booking?.number}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  {/* Shared property type badge */}
                  {isShared && (
                    <div style={{
                      padding: "5px 12px", borderRadius: 100,
                      background: "#eff6ff", color: "#3b82f6",
                      fontSize: 11, fontWeight: 700, border: "1px solid #bfdbfe",
                    }}>
                      🏠 Shared
                    </div>
                  )}
                  <div style={{
                    padding: "6px 14px", borderRadius: 100,
                    background: "#f0fdf4", color: "#16a34a",
                    fontSize: 12, fontWeight: 700, border: "1px solid #bbf7d0",
                  }}>✓ Paid</div>
                </div>
              </div>

              {/* ── Room name row — only for shared properties ── */}
              {isShared && roomName && (
                <DetailRow
                  delay={0.05}
                  label="Your Room"
                  value={roomName}
                  tag={{ text: "Shared Property", bg: "#eff6ff", color: "#3b82f6" }}
                  icon={<IconDoor />}
                />
              )}

              {booking?.check_in_date && (
                <DetailRow delay={0.1} label="Check-in"
                  value={moment(booking.check_in_date).format("ddd, MMM D, YYYY")}
                  icon={<IconCalendar />} />
              )}
              {booking?.check_out_date && (
                <DetailRow delay={0.15} label="Check-out"
                  value={moment(booking.check_out_date).format("ddd, MMM D, YYYY")}
                  icon={<IconCalendar />} />
              )}
              {booking?.no_of_months && (
                <DetailRow delay={0.2} label="Duration"
                  value={`${booking.no_of_months} ${booking.no_of_months === 1 ? "month" : "months"}`}
                  icon={<IconClock />} />
              )}
              {booking?.number_of_guests && (
                <DetailRow delay={0.25} label="Guests"
                  value={`${booking.number_of_guests} ${booking.number_of_guests === 1 ? "guest" : "guests"}`}
                  icon={<IconGuests />} />
              )}
              {booking?.property?.address && (
                <DetailRow delay={0.3} label="Address"
                  value={booking.property.address}
                  icon={<IconPin />} />
              )}
              {booking?.invoice?.invoice_period && (
                <DetailRow delay={0.35} label="Invoice Period"
                  value={booking.invoice.invoice_period}
                  icon={<IconDoc />} />
              )}
              {intentId && (
                <DetailRow delay={0.4} label="Transaction ID"
                  value={intentId}
                  icon={<IconCard />} />
              )}

              {/* What's next */}
              <div style={{
                marginTop: 24, padding: "16px", borderRadius: 14,
                background: "#f8fafc", border: "1px solid #e2e8f0",
                animation: showContent ? "slideUp 0.4s 0.45s both" : "none", opacity: 0,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#475569",
                  textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 10 }}>
                  What's next
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none",
                  display: "flex", flexDirection: "column", gap: 8 }}>
                  {[
                    "A confirmation email has been sent to you",
                    "Your host will be notified of your booking",
                    "Review your booking details anytime in the dashboard",
                  ].map((item, i) => (
                    <li key={i} style={{ display: "flex", gap: 8, fontSize: 13, color: "#64748b" }}>
                      <span style={{ color: "#22c55e", fontWeight: 700, flexShrink: 0 }}>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTAs */}
              <div style={{
                marginTop: 20, display: "flex", flexDirection: "column", gap: 10,
                animation: showContent ? "slideUp 0.4s 0.5s both" : "none", opacity: 0,
              }}>
                <button type="button"
                  onClick={() => window.location.href = `/booking_request?id=${booking?.id}`}
                  style={{
                    width: "100%", padding: "14px", borderRadius: 14,
                    border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg, #22c55e, #16a34a)",
                    color: "white", fontSize: 15, fontWeight: 700,
                    boxShadow: "0 4px 20px rgba(34,197,94,0.3)",
                    transition: "transform 0.15s ease, box-shadow 0.15s ease",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 6px 24px rgba(34,197,94,0.4)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 20px rgba(34,197,94,0.3)";
                  }}
                >
                  View Booking Details →
                </button>

                {/* Download receipt — hits Rails endpoint */}
                <button type="button"
                  onClick={handleDownloadReceipt}
                  disabled={downloading}
                  style={{
                    width: "100%", padding: "13px", borderRadius: 14,
                    border: "1.5px solid #e2e8f0", cursor: downloading ? "not-allowed" : "pointer",
                    background: "white", color: "#475569", fontSize: 14, fontWeight: 600,
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    transition: "border-color 0.15s ease, color 0.15s ease",
                    opacity: downloading ? 0.7 : 1,
                  }}
                  onMouseEnter={e => {
                    if (!downloading) {
                      e.currentTarget.style.borderColor = "#22c55e";
                      e.currentTarget.style.color = "#16a34a";
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = "#e2e8f0";
                    e.currentTarget.style.color = "#475569";
                  }}
                >
                  {downloading ? (
                    <>
                      <div style={{
                        width: 15, height: 15,
                        border: "2px solid #e2e8f0", borderTopColor: "#22c55e",
                        borderRadius: "50%", animation: "spin 0.7s linear infinite", flexShrink: 0,
                      }} />
                      Generating PDF…
                    </>
                  ) : (
                    <><IconDownload /> Download Receipt (PDF)</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default PaymentSuccess;